import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { supabase, authHelpers } from "../lib/supabase";
import authService from "../shared/services/authService";
import logger from "../shared/utils/logger";
import { metaTrack, registrationEventId } from "../shared/utils/metaPixel";
import { getStoredAcquisition } from "../shared/utils/acquisitionUtils";
import { getStoredReferralCode } from "../shared/utils/referralUtils";
import { consumeSignupAttestationFlag } from "../shared/utils/signupAttestation";
import { requestBalanceRefresh } from "../shared/utils/balanceEvents";
import { geoReverify } from "../shared/services/geoVerificationService";
import {
  trackSessionStart,
  trackEvent,
} from "../shared/services/playerEvents";

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Generate username helper function
const generateUsername = () => {
  // 64 adjectives
  const adjectives = [
    "Swift",
    "Brave",
    "Clever",
    "Lucky",
    "Bold",
    "Wise",
    "Quick",
    "Sharp",
    "Bright",
    "Strong",
    "Cool",
    "Wild",
    "Free",
    "Pure",
    "True",
    "Fast",
    "Silent",
    "Fierce",
    "Noble",
    "Mighty",
    "Agile",
    "Stealth",
    "Cosmic",
    "Mystic",
    "Golden",
    "Silver",
    "Crystal",
    "Shadow",
    "Blazing",
    "Frozen",
    "Electric",
    "Sonic",
    "Ancient",
    "Royal",
    "Savage",
    "Gentle",
    "Deadly",
    "Peaceful",
    "Raging",
    "Calm",
    "Crimson",
    "Azure",
    "Emerald",
    "Violet",
    "Amber",
    "Ivory",
    "Obsidian",
    "Platinum",
    "Legendary",
    "Epic",
    "Heroic",
    "Valiant",
    "Fearless",
    "Daring",
    "Cunning",
    "Sly",
    "Radiant",
    "Glowing",
    "Shining",
    "Sparkling",
    "Gleaming",
    "Brilliant",
    "Luminous",
    "Fat",
  ];

  // 32 animals
  const animals = [
    "Eagle",
    "Wolf",
    "Tiger",
    "Lion",
    "Bear",
    "Fox",
    "Hawk",
    "Shark",
    "Falcon",
    "Panther",
    "Viper",
    "Raven",
    "Jaguar",
    "Leopard",
    "Cheetah",
    "Lynx",
    "Cobra",
    "Python",
    "Scorpion",
    "Spider",
    "Mantis",
    "Wasp",
    "Hornet",
    "Beetle",
    "Dolphin",
    "Whale",
    "Octopus",
    "Barracuda",
    "Piranha",
    "Stingray",
    "Hammerhead",
    "Pig",
  ];

  // 32 objects/concepts
  const objects = [
    "Phoenix",
    "Dragon",
    "Storm",
    "Flame",
    "Thunder",
    "Lightning",
    "Blizzard",
    "Tornado",
    "Sword",
    "Shield",
    "Arrow",
    "Blade",
    "Hammer",
    "Axe",
    "Spear",
    "Dagger",
    "Crown",
    "Throne",
    "Castle",
    "Tower",
    "Fortress",
    "Palace",
    "Temple",
    "Shrine",
    "Star",
    "Moon",
    "Sun",
    "Comet",
    "Galaxy",
    "Nebula",
    "Meteor",
    "Cosmos",
  ];

  // Randomly choose from animals or objects
  const nouns = Math.random() < 0.5 ? animals : objects;

  const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const number = Math.floor(1000 + Math.random() * 9000); // 4-digit number

  return `${adjective}${noun}${number}`;
};

// SECURITY: admin hint is read from app_metadata ONLY.
// user_metadata is user-writable via supabase.auth.updateUser and must never be
// trusted as a signal for admin UI. This function is only used to decide whether
// to attempt /api/admin/me; the backend re-validates against players.admin_role.
const isAdminUser = (user) => {
  return (
    user &&
    (user.app_metadata?.is_admin === true ||
      user.app_metadata?.is_admin === "true")
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  // Admin-specific state for RBAC
  const [adminRole, setAdminRole] = useState(null);
  const [adminPermissions, setAdminPermissions] = useState([]);

  // Social-login DOB gate. Google OAuth users never supply a date of birth,
  // so the backend age gate refuses to stamp geo_verified=true and every
  // player endpoint 403s. When the account needs a DOB we flip this on, which
  // renders the blocking DobGate sheet until the user provides a valid 18+ DOB.
  const [needsDob, setNeedsDob] = useState(false);

  // Didit phone gate (impassable, ALL players). /api/auth/verify reports
  // needsPhone=true for any active player who has not passed phone verification;
  // the blocking PhoneGate then holds the whole app until they complete the Didit
  // phone flow (webhook flips phone_verified, next /verify clears the gate).
  const [needsPhone, setNeedsPhone] = useState(false);

  // player_events funnel: phone_gate_completed, fired off the gate's own
  // true -> false transition rather than from the Didit callback page.
  //
  // The callback page is the wrong place for it twice over. It only runs if
  // Didit actually returns the player to DIDIT_CALLBACK_URL, and it fired one
  // statement before a window.location.assign, so the request died with the
  // document. Result: 405 real phone verifications, zero recorded completions.
  //
  // Here it fires wherever the gate clears, including on a later visit if the
  // player closed the tab mid-flow. The ref means we only report a clearing we
  // actually watched happen, never the initial false on mount, and re-arming it
  // is what makes a re-wall visible (a phone_gate_shown AFTER a completion).
  const sawPhoneGateRef = useRef(false);
  useEffect(() => {
    if (needsPhone) {
      sawPhoneGateRef.current = true;
      return;
    }
    if (sawPhoneGateRef.current) {
      sawPhoneGateRef.current = false;
      trackEvent("phone_gate_completed", { via: "gate_cleared" });
    }
  }, [needsPhone]);

  // Live geo re-verification gate (Geo-Verification v2, feature doc 23).
  // Founder policy: GEO BLOCKS LOGIN. On the SIGNED_IN event (and session
  // restore) we hit /api/auth/geo-reverify, which re-checks the player's LIVE
  // location server-side. Only an explicit `geo_blocked` verdict flips this on,
  // rendering the blocking GeoRestrictedGate. A 429, a network error, or an
  // inconclusive / VPN result all FAIL OPEN (never block) so a legit player
  // (incl. the founder on a US VPN) is never locked out. Admins are exempt and
  // never run this check. `geoBlockInfo` carries the detected country/region for
  // the gate's display only.
  const [geoBlocked, setGeoBlocked] = useState(false);
  const [geoBlockInfo, setGeoBlockInfo] = useState(null);

  // Fire the live geo re-verification and drive the gate off the server verdict.
  // Non-blocking: only sets state, never throws. Callers do NOT await it so it
  // can't delay first paint or the rest of the sign-in flow. Admins must NOT
  // call this (they are geo-exempt).
  const runGeoReverify = async (accessToken) => {
    if (!accessToken) return;
    const result = await geoReverify(accessToken);
    if (result.blocked) {
      // Explicit live-restricted verdict: hold the user on the blocked screen.
      setGeoBlockInfo({ country: result.country, region: result.region });
      setGeoBlocked(true);
    } else {
      // allow / dob_required / vpn / inconclusive / any error → do not block.
      // Also clears a stale block if the user is now on a clean allowed IP
      // (the falsely-frozen-VPN-user heals here). DOB handling is untouched and
      // stays owned by checkDobGate + the registration trigger.
      setGeoBlocked(false);
      setGeoBlockInfo(null);
    }
  };

  // Authoritative DOB-gate check. /api/auth/verify is cheap, auth-gated (NOT
  // requirePlayer, so it works while the account is geo-locked) and NOT
  // rate-limited — unlike trigger-registration-event (5/15min), which can 429
  // and silently mask the lockout. Runs on both fresh sign-in and session
  // restore so new AND already-locked accounts surface the gate uniformly.
  const checkDobGate = async (accessToken) => {
    if (!accessToken) return;
    try {
      const res = await fetch("/api/auth/verify", {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        // Drive the flags directly off the server's truth so they also CLEAR
        // once the account is healed (DOB submitted, phone verified).
        setNeedsDob(!!body?.needsDob);
        // Phone gate comes AFTER the DOB gate: never raise it while a DOB is
        // still owed (the two would stack). Cleared the moment the webhook flips
        // phone_verified and the next /verify returns needsPhone=false.
        setNeedsPhone(!body?.needsDob && !!body?.needsPhone);
      }
    } catch (err) {
      logger.warn("DOB-gate check failed", { error: err.message });
    }
  };

  // Simple session tracking to prevent multiple calls in the same session
  const sessionTriggeredRef = useRef(new Set());

  // Track the last processed auth event to prevent duplicate processing
  const lastProcessedEventRef = useRef(null);
  const authEventTimeoutRef = useRef(null);

  // Fetch admin role/permissions from backend
  // Accepts an optional accessToken so callers inside onAuthStateChange can pass
  // the token directly instead of calling getSession() (which can deadlock).
  const fetchAdminInfo = async (accessToken) => {
    try {
      const token =
        accessToken ??
        (await supabase.auth.getSession()).data.session?.access_token;
      const response = await fetch("/api/admin/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAdminRole(data.role);
        setAdminPermissions(data.permissions || []);
        logger.info("Admin info fetched", {
          role: data.role,
          permissionCount: data.permissions?.length,
        });
      } else {
        // SECURITY: fail closed. If the backend doesn't confirm admin role, do NOT
        // grant admin UI. Any user with a stale app_metadata.is_admin hint will see
        // the "not authorized" path here, which is the intended outcome.
        logger.warn("Admin info fetch denied — clearing admin state", {
          status: response.status,
        });
        setAdminRole(null);
        setAdminPermissions([]);
      }
    } catch (error) {
      logger.warn("Failed to fetch admin info — clearing admin state", {
        error: error.message,
      });
      setAdminRole(null);
      setAdminPermissions([]);
    }
  };

  useEffect(() => {
    // Get initial session with reasonable timeout for page refreshes
    const getInitialSession = async () => {
      try {
        // Use our authService instead of calling Supabase directly
        const { session } = await authService.getSession();
        const sessionError = null; // authService handles errors internally

        if (sessionError) {
          logger.error("AuthContext: Session error", sessionError);
        }

        const currentUser = session?.user ?? null;

        // Log session restoration for debugging
        if (currentUser) {
          logger.success("Session restored successfully on page load", {
            userId: currentUser.id,
            email: currentUser.email,
          });
        } else {
          logger.info("No session found on page load");
        }

        setUser(currentUser);
        const isAdmin = isAdminUser(currentUser);
        setUserRole(isAdmin ? "admin" : "player");

        // Fetch admin permissions if admin user
        if (isAdmin) {
          await fetchAdminInfo();
        } else {
          setAdminRole(null);
          setAdminPermissions([]);

          // Detect the social-login DOB lockout on session RESTORE (page load).
          await checkDobGate(session?.access_token);

          // Live geo re-verification on session restore too, so a page reload
          // both HEALS a falsely-frozen account and re-blocks a user who is now
          // in a restricted location. Fire-and-forget (players only; admins are
          // handled by the isAdmin branch above and never reach here).
          runGeoReverify(session?.access_token);

          // player_events funnel: mark the visit start on session restore.
          // Fire-and-forget, deduped to once per tab-session inside the helper.
          if (currentUser?.id) trackSessionStart();
        }

        setLoading(false);
      } catch (error) {
        logger.error("Error in getInitialSession:", error);
        // Always set loading to false, assume no user
        setUser(null);
        setUserRole("player");
        setAdminRole(null);
        setAdminPermissions([]);
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes with timeout protection
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      logger.info("Auth state change event", {
        event,
        userId: session?.user?.id,
      });

      const currentUser = session?.user ?? null;
      const eventKey = `${event}-${currentUser?.id || "null"}`;

      // Debounce duplicate events within 1 second
      if (lastProcessedEventRef.current === eventKey) {
        return;
      }

      // Clear any existing timeout
      if (authEventTimeoutRef.current) {
        clearTimeout(authEventTimeoutRef.current);
      }

      // Set a timeout to reset the debounce after 1 second
      authEventTimeoutRef.current = setTimeout(() => {
        lastProcessedEventRef.current = null;
      }, 1000);

      lastProcessedEventRef.current = eventKey;

      // Status check removed - we use realtime subscription instead (see below)

      setUser(currentUser);
      const isAdmin = isAdminUser(currentUser);
      setUserRole(isAdmin ? "admin" : "player");

      // Fetch admin permissions if admin (mirrors getInitialSession logic)
      // Pass the token from the callback to avoid calling getSession() inside
      // onAuthStateChange, which can deadlock Supabase's auth client.
      if (isAdmin && !adminRole) {
        await fetchAdminInfo(session?.access_token);
      }

      setLoading(false);

      // Trigger registration + daily login events on sign in (backend handles deduplication)
      // Skip for admin users — these events are player-only
      if (event === "SIGNED_IN" && currentUser?.id && !isAdmin) {
        // Authoritative DOB-gate check on every fresh sign-in. Runs BEFORE the
        // once-per-session guard below (and independent of the rate-limited
        // registration trigger) so already-locked accounts reliably get the
        // blocking sheet the moment they log back in.
        await checkDobGate(session?.access_token);

        // Live geo re-verification on fresh sign-in (feature doc 23, WS-3).
        // Founder policy: GEO BLOCKS LOGIN. Runs BEFORE the once-per-session
        // guard below so every login is re-checked. Fire-and-forget so the
        // allow/inconclusive path never blocks the rest of sign-in or first
        // paint. Only an explicit geo_blocked verdict raises the gate, and
        // network/429 failures fail open. Placed inside the `!isAdmin` block,
        // so admins are never geo-checked and can never be locked out.
        runGeoReverify(session?.access_token);

        // player_events funnel: mark the visit start on fresh sign-in.
        // Fire-and-forget, deduped to once per tab-session inside the helper.
        trackSessionStart();

        // Simple session check to prevent multiple calls in the same browser session
        if (sessionTriggeredRef.current.has(currentUser.id)) {
          logger.info("Sign-in events already called this session, skipping", {
            userId: currentUser.id,
            event,
          });
          return;
        }

        // Mark as called in this session
        sessionTriggeredRef.current.add(currentUser.id);

        // Trigger registration event for new users (backend deduplicates)
        if (session?.user) {
          // 18+ attestation moved to the signup page: a Google signup started
          // from /signup persists a one-shot sessionStorage flag before the
          // OAuth redirect. Consume it here (read-and-clear) so we can stamp
          // age_verified inline via over18_attested and this new user never
          // sees the DobGate popup. Only the SIGN UP surface sets the flag, so
          // an existing user signing in is never wrongly attested. If the flag
          // was lost (blocked storage, non-signup entry point), the DobGate
          // fallback still covers the account. Cleared exactly once regardless
          // of the request outcome.
          const over18Attested = consumeSignupAttestationFlag();

          try {
            const registrationResponse = await fetch(
              "/api/auth/trigger-registration-event",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                  userId: currentUser.id,
                  // Affirmative 18+ attestation from the signup page (Google
                  // path). Only sent when the signup flag was present; the
                  // backend stamps age_verified from it when no DOB exists and
                  // never overrides a known DOB. Omitted (false) otherwise.
                  over18_attested: over18Attested,
                  userData: {
                    email: currentUser.email,
                    username: currentUser.user_metadata?.username,
                    country: currentUser.user_metadata?.country,
                    // Fall back to storage: OAuth (Google/social) signups never
                    // have referral_code in user_metadata (only the email form
                    // sets it), so read it fresh like we do for acquisition.
                    referral_code:
                      currentUser.user_metadata?.referral_code ||
                      getStoredReferralCode(),
                  },
                  // Acquisition source (UTM / referrer) captured on first touch;
                  // backend reads req.body.acquisition. Null when unattributed.
                  acquisition: getStoredAcquisition(),
                }),
              },
            );

            const result = await registrationResponse.json().catch(() => ({}));
            if (registrationResponse.status === 200) {
              // 200 (triggered or already-registered) means the account is not
              // DOB-locked — the trigger heals metadata-DOB accounts, so clear
              // a gate that checkDobGate raised moments earlier or the blocking
              // sheet stays up over a healthy account. (202 deferred is NOT
              // success — provisioning race, state unknown.)
              setNeedsDob(false);
              // The signup Gold Coin grant is applied server-side during this
              // trigger; the realtime balance subscription won't fire for it,
              // so nudge useBalances to refetch (with retry) or the header
              // shows 0 GLD until a manual reload.
              requestBalanceRefresh();
            }
            if (registrationResponse.ok && result.triggered) {
              logger.success("Registration event triggered successfully", {
                userId: currentUser.id,
                referralAttributed: result.referralAttributed,
              });
              // Genuinely-new registration only (result.triggered is true just
              // once per user lifetime, server-side). Fire the browser Meta
              // CompleteRegistration here so it never fires for the ~12k existing
              // users signing in post-deploy. Deduped against the server CAPI
              // event via the deterministic registration_<userId> eventID.
              metaTrack(
                "CompleteRegistration",
                {},
                { eventID: registrationEventId(currentUser.id) },
              );
            } else if (
              registrationResponse.status === 403 &&
              result?.errorCode === "DOB_REQUIRED"
            ) {
              // Social-login user without a DOB on file — surface the blocking
              // DOB gate. They cannot use any player endpoint until resolved.
              logger.warn("Registration requires DOB — opening DOB gate", {
                userId: currentUser.id,
              });
              setNeedsDob(true);
            }
          } catch (error) {
            logger.error("Registration event trigger error", error);
          }
        }

        logger.info("Calling daily login trigger", {
          userId: currentUser.id,
          event,
          timestamp: new Date().toISOString(),
        });

        try {
          // Call backend - it will handle deduplication
          const response = await fetch("/api/auth/trigger-daily-login", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              userId: currentUser.id,
              loginMethod: "supabase_auth",
              triggeredBy: "auth_state_change",
            }),
          });

          // Fire-and-forget marketing sync (idempotent). We don't block UX on this.
          fetch("/api/marketing/sync-contact", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            // Optionally pass metadata in case backend wants it (JWT may not include it)
            body: JSON.stringify({
              user_metadata: currentUser?.user_metadata || {},
            }),
          }).catch(() => {});

          if (!response.ok) {
            logger.error("Daily login trigger failed", response.status);
            // Remove from session tracking so it can be retried
            sessionTriggeredRef.current.delete(currentUser.id);
          } else {
            logger.success("Daily login trigger call completed", {
              userId: currentUser.id,
              timestamp: new Date().toISOString(),
            });
          }
        } catch (error) {
          logger.error("Daily login trigger error", error);
          // Remove from session tracking so it can be retried
          sessionTriggeredRef.current.delete(currentUser.id);
        }
      }

      // Clear session tracking when user signs out
      if (event === "SIGNED_OUT") {
        sessionTriggeredRef.current.clear();
        setGeoBlocked(false);
        setGeoBlockInfo(null);
        setNeedsPhone(false);
      }
    });

    return () => {
      subscription.unsubscribe();
      // Clean up timeout
      if (authEventTimeoutRef.current) {
        clearTimeout(authEventTimeoutRef.current);
      }
    };
  }, []);

  const signUp = async (userData) => {
    try {
      // Generate username
      const username = generateUsername();

      // Prepare user metadata - only include fields that are provided
      // City, address, zip_code, and date_of_birth will be populated from Sumsub KYC verification
      const userMetadata = {
        username,
        first_name: userData.firstName,
        last_name: userData.lastName,
        country: userData.country,
        state: userData.state,
        receive_emails: userData.receiveEmails,
      };

      // Only include optional fields if they are provided
      if (userData.city) userMetadata.city = userData.city;
      if (userData.address) userMetadata.address = userData.address;
      if (userData.zipCode) userMetadata.zip_code = userData.zipCode;
      if (userData.dateOfBirth)
        userMetadata.date_of_birth = userData.dateOfBirth;
      if (userData.referralCode)
        userMetadata.referral_code = userData.referralCode;

      const { data, error } = await authHelpers.signUp(
        userData.email,
        userData.password,
        userMetadata,
      );

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      logger.error("Sign up error:", error);
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    setLoading(true);
    try {
      const { data, error } = await authHelpers.signIn(email, password);

      if (error) throw error;

      const currentUser = data?.user ?? null;
      const session = data?.session ?? null;

      // After Supabase auth succeeds, verify account status via backend.
      // This ensures suspended/banned/inactive users can never complete login.
      // (No try/catch wrapper here — the outer signIn try/catch handles
      // network errors and we re-throw status verification failures from
      // inside this block as plain Error so callers can render them.)
      if (session?.access_token && currentUser) {
        const verifyResponse = await fetch("/api/auth/verify", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        const verifyResult = await verifyResponse.json().catch(() => ({}));

        if (!verifyResponse.ok) {
          const message =
            verifyResult?.message || verifyResult?.error || "Login failed";

          // Fully sign the user out so suspended accounts are not treated as logged in
          try {
            await authService.signOut();
          } catch (signOutError) {
            logger.error(
              "Error during sign-out after failed status verification",
                signOutError,
              );
            }

            // Clear local auth state immediately
            setUser(null);
            setUserRole("player");

            throw new Error(message);
          }
        }

      // Only update auth state after successful status verification
      setUser(currentUser);
      const isAdmin = isAdminUser(currentUser);
      setUserRole(isAdmin ? "admin" : "player");

      // Fetch admin role/permissions so the sidebar is ready immediately after sign-in
      // without requiring a page refresh
      if (isAdmin) {
        await fetchAdminInfo();
      }

      setLoading(false);

      logger.success("Sign in successful", {
        userId: currentUser?.id,
        email: currentUser?.email,
      });

      return { data, error: null };
    } catch (error) {
      logger.error("Sign in error:", error);

      // Ensure loading state is cleared even on error
      setLoading(false);

      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      // Immediately clear auth context so UI reacts without waiting for any async
      setUser(null);
      setUserRole("player");
      setAdminRole(null);
      setAdminPermissions([]);
      setLoading(false);
      setGeoBlocked(false);
      setGeoBlockInfo(null);
      sessionTriggeredRef.current.clear();

      // Perform async cleanup in background with timeout-safe service
      await authService.signOut();
    } catch (error) {
      logger.error("Sign out error:", error);
      // Do not rethrow to avoid blocking UI
    }
  };

  // Helper function to check if current admin has a permission
  const hasPermission = (requiredPermission) => {
    if (!adminPermissions || !Array.isArray(adminPermissions)) return false;
    // Wildcard grants all permissions
    if (adminPermissions.includes("*")) return true;
    // Check if any required permission is granted
    const required = Array.isArray(requiredPermission)
      ? requiredPermission
      : [requiredPermission];
    return required.some((perm) => adminPermissions.includes(perm));
  };

  // Complete the social-login DOB gate. Re-fires the registration trigger with
  // the supplied date of birth; on success the backend stamps geo_verified=true
  // and the player is unblocked. Returns a structured result so the DobGate
  // sheet can branch on the outcome (success / under-age / region-blocked).
  const completeRegistrationDob = async (dateOfBirth) => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      const uid = session?.user?.id;
      if (!token || !uid) {
        return {
          ok: false,
          errorCode: "NO_SESSION",
          message: "Your session expired. Please sign in again.",
        };
      }

      const res = await fetch("/api/auth/trigger-registration-event", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: uid,
          userData: {
            email: session.user?.email,
            username: session.user?.user_metadata?.username,
            date_of_birth: dateOfBirth,
            // OAuth users have no referral_code in user_metadata, and this
            // DOB-completion retry is the call that actually persists for them.
            // Without this the source code (?ref=) is dropped for every social
            // signup even when they arrived via a tagged ad link.
            referral_code: getStoredReferralCode(),
          },
          // Social-login (Google) users hit the DOB gate on their first
          // trigger-registration-event, which throws BEFORE the acquisition
          // persist runs. This DOB-completion retry is the call that actually
          // reaches the persist block, so it MUST carry the acquisition too or
          // every OAuth signup is recorded as unattributed.
          acquisition: getStoredAcquisition(),
        }),
      });

      const body = await res.json().catch(() => ({}));
      if (res.status === 202 || body?.deferred) {
        // Players row not provisioned yet — the backend stamped NOTHING (the
        // DOB was not persisted). Treating this as success cleared the gate and
        // sent the user in a reload loop that lost their input. Keep the gate
        // up and let the caller retry shortly.
        return {
          ok: false,
          deferred: true,
          status: res.status,
          errorCode: "DEFERRED",
          message:
            "Your account is still being set up. Please try again in a few seconds.",
        };
      }
      if (res.ok) {
        setNeedsDob(false);
        // DOB-completion is the call that actually provisions social-login
        // accounts and applies the signup grant, so refetch the balance here
        // too (same reason as the normal registration path above).
        requestBalanceRefresh();
        return { ok: true };
      }
      return {
        ok: false,
        status: res.status,
        errorCode: body?.errorCode || null,
        message: body?.error || body?.message || "Something went wrong.",
      };
    } catch (error) {
      logger.error("completeRegistrationDob error", error);
      return {
        ok: false,
        errorCode: "NETWORK",
        message: "Something went wrong. Please try again.",
      };
    }
  };

  // Complete the social-login age gate via a ONE-TAP 18+ attestation (no typed
  // DOB). Google OAuth carries no date of birth, so the account is DOB-locked;
  // the affirmative tap on the clearly-labelled 18+ button IS the consent. This
  // mirrors completeRegistrationDob exactly (same endpoint, same {ok, errorCode,
  // message} contract, same terminal-state mapping) except the body sends
  // `over18_attested: true` and NO date_of_birth. The backend stamps age_verified
  // from the attestation; hard age is still verified by ID at KYC before any
  // redemption. Kept alongside completeRegistrationDob, which the email-signup
  // path still uses.
  const completeRegistrationAttestation = async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      const uid = session?.user?.id;
      if (!token || !uid) {
        return {
          ok: false,
          errorCode: "NO_SESSION",
          message: "Your session expired. Please sign in again.",
        };
      }

      const res = await fetch("/api/auth/trigger-registration-event", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          // Affirmative 18+ attestation in place of a typed DOB. Backend sets
          // age_verified from this flag.
          over18_attested: true,
          userId: uid,
          userData: {
            email: session.user?.email,
            username: session.user?.user_metadata?.username,
            // OAuth users have no referral_code in user_metadata, and this
            // attestation retry is the call that actually persists for them.
            // Without it the source code (?ref=) is dropped for every social
            // signup even when they arrived via a tagged ad link.
            referral_code: getStoredReferralCode(),
          },
          // Social-login (Google) users hit the age gate on their first
          // trigger-registration-event, which throws BEFORE the acquisition
          // persist runs. This attestation retry is the call that actually
          // reaches the persist block, so it MUST carry the acquisition too or
          // every OAuth signup is recorded as unattributed.
          acquisition: getStoredAcquisition(),
        }),
      });

      const body = await res.json().catch(() => ({}));
      if (res.status === 202 || body?.deferred) {
        // Players row not provisioned yet: the backend stamped NOTHING.
        // Treating this as success would clear the gate and send the user into
        // a reload loop. Keep the gate up and let the caller retry shortly.
        return {
          ok: false,
          deferred: true,
          status: res.status,
          errorCode: "DEFERRED",
          message:
            "Your account is still being set up. Please try again in a few seconds.",
        };
      }
      if (res.ok) {
        setNeedsDob(false);
        // Attestation is the call that provisions social-login accounts and
        // applies the signup grant, so refetch the balance here too.
        requestBalanceRefresh();
        return { ok: true };
      }
      return {
        ok: false,
        status: res.status,
        errorCode: body?.errorCode || null,
        message: body?.error || body?.message || "Something went wrong.",
      };
    } catch (error) {
      logger.error("completeRegistrationAttestation error", error);
      return {
        ok: false,
        errorCode: "NETWORK",
        message: "Something went wrong. Please try again.",
      };
    }
  };

  // Start the Didit phone-verification flow. Creates a hosted session and
  // returns its URL so the PhoneGate can redirect the player to it. Structured
  // result ({ ok, url, message }) mirrors the other gate actions.
  const startPhoneVerification = async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        return {
          ok: false,
          errorCode: "NO_SESSION",
          message: "Your session expired. Please sign in again.",
        };
      }
      const res = await fetch("/api/casino/kyc/phone/start", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body?.url) {
        return { ok: true, url: body.url };
      }
      // No url because there is nothing left to verify: the server reconciled a
      // Didit approval whose webhook never landed. Not an error, and showing it
      // as one is what left players pressing a verify button that could never
      // do anything. Refresh the gates so the wall comes down on the spot.
      if (res.ok && body?.verified) {
        await refreshAuthGates();
        return { ok: true, alreadyVerified: true };
      }
      return {
        ok: false,
        message:
          body?.error ||
          body?.message ||
          "Phone verification is unavailable right now. Please try again shortly.",
      };
    } catch (error) {
      logger.error("startPhoneVerification error", error);
      return {
        ok: false,
        errorCode: "NETWORK",
        message: "Something went wrong. Please try again.",
      };
    }
  };

  // Re-run the server gate check (needsDob / needsPhone) against the current
  // session. Used by the Didit callback page to poll until the webhook has
  // flipped phone_verified, then let the player into the app.
  const refreshAuthGates = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    await checkDobGate(session?.access_token);
  };

  const value = {
    user,
    loading,
    userRole,
    signUp,
    signIn,
    signOut,
    isLoggedIn: !!user,
    isAdmin: userRole === "admin",
    // Social-login age gate
    needsDob,
    completeRegistrationDob,
    completeRegistrationAttestation,
    // Didit phone gate
    needsPhone,
    startPhoneVerification,
    refreshAuthGates,
    // Live geo-restricted gate (Geo-Verification v2)
    geoBlocked,
    geoBlockInfo,
    // Admin RBAC
    adminRole,
    adminPermissions,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
